/**
 * @fileoverview Statistics component for room KPIs and utilization metrics
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 */

import React from 'react';
import {
  Paper,
  SimpleGrid,
  Stack,
  Group,
  Text,
  Progress,
  ThemeIcon,
  Title,
  Button,
  Alert,
  LoadingOverlay,
  Center,
  Badge,
  Table,
  ScrollArea
} from '@mantine/core';
import {
  IconDoor,
  IconUsers,
  IconBuilding,
  IconTrendingUp,
  IconTrendingDown,
  IconRefresh,
  IconAlertCircle,
  IconChartBar,
  IconCalendarStats
} from '@tabler/icons-react';
import type { RoomStats as RoomStatsData } from '../../api/endpoints/rooms';

/**
 * Props for RoomStats component
 */
interface RoomStatsProps {
  stats?: RoomStatsData;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
}

/**
 * Individual stat card component
 */
interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  description,
  icon,
  color,
  trend
}) => {
  return (
    <Paper p="lg" radius="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Text size="sm" c="dimmed" fw={500}>
              {title}
            </Text>
            <Text size="xl" fw={700} c={color}>
              {value}
            </Text>
            {description && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </div>
          
          <ThemeIcon
            size="lg"
            radius="md"
            color={color}
            variant="light"
          >
            {icon}
          </ThemeIcon>
        </Group>
        
        {trend && (
          <Group gap="xs">
            {trend.direction === 'up' && <IconTrendingUp size="0.875rem" color="var(--mantine-color-green-6)" />}
            {trend.direction === 'down' && <IconTrendingDown size="0.875rem" color="var(--mantine-color-red-6)" />}
            <Text size="xs" c={trend.direction === 'up' ? 'green' : trend.direction === 'down' ? 'red' : 'dimmed'}>
              {trend.direction === 'up' ? '+' : ''}{trend.value}%
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Statistics component for room KPIs and utilization metrics
 * Displays comprehensive room statistics with modern Apple-inspired design
 */
export const RoomStats: React.FC<RoomStatsProps> = ({
  stats,
  loading = false,
  error,
  onRefresh
}) => {
  // Loading state
  if (loading && !stats) {
    return (
      <Paper radius="md" withBorder>
        <LoadingOverlay visible />
        <div style={{ height: '400px' }} />
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper radius="md" withBorder p="lg">
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title="Error Loading Statistics"
          color="red"
          variant="light"
          {...(onRefresh && {
            action: (
              <Button
                variant="outline"
                size="xs"
                leftSection={<IconRefresh size="0.875rem" />}
                onClick={onRefresh}
              >
                Retry
              </Button>
            )
          })}
        >
          Failed to load room statistics. Please try again.
        </Alert>
      </Paper>
    );
  }

  // Empty state
  if (!stats) {
    return (
      <Paper radius="md" withBorder p="xl">
        <Center>
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" radius="md" color="gray" variant="light">
              <IconChartBar size="2rem" />
            </ThemeIcon>
            <div style={{ textAlign: 'center' }}>
              <Text size="lg" fw={500} c="dimmed">
                No Statistics Available
              </Text>
              <Text size="sm" c="dimmed">
                Statistics will appear here once rooms are created
              </Text>
            </div>
          </Stack>
        </Center>
      </Paper>
    );
  }

  // Calculate utilization percentage
  const utilizationPercentage = stats.totalRooms > 0 
    ? Math.round((stats.activeRooms / stats.totalRooms) * 100) 
    : 0;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" align="center">
        <div>
          <Title order={3} fw={600}>
            Room Statistics
          </Title>
          <Text c="dimmed" size="sm">
            Overview of room utilization and capacity metrics
          </Text>
        </div>
        
        {onRefresh && (
          <Button
            variant="outline"
            size="sm"
            leftSection={<IconRefresh size="1rem" />}
            onClick={onRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        )}
      </Group>

      {/* Main KPI Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          title="Total Rooms"
          value={stats.totalRooms}
          description="All rooms in the system"
          icon={<IconDoor size="1.25rem" />}
          color="blue"
        />
        
        <StatCard
          title="Active Rooms"
          value={stats.activeRooms}
          description={`${utilizationPercentage}% of total rooms`}
          icon={<IconUsers size="1.25rem" />}
          color="green"
          trend={{
            value: utilizationPercentage,
            direction: utilizationPercentage >= 80 ? 'up' : utilizationPercentage < 50 ? 'down' : 'neutral'
          }}
        />
        
        <StatCard
          title="Total Capacity"
          value={stats.totalCapacity}
          description="Maximum occupancy across all rooms"
          icon={<IconBuilding size="1.25rem" />}
          color="orange"
        />
        
        <StatCard
          title="Average Capacity"
          value={stats.averageCapacity}
          description="Per room capacity average"
          icon={<IconCalendarStats size="1.25rem" />}
          color="purple"
        />
      </SimpleGrid>

      {/* Utilization Progress */}
      <Paper p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Text fw={600}>Room Utilization</Text>
            <Badge color={utilizationPercentage >= 80 ? 'green' : utilizationPercentage >= 60 ? 'yellow' : 'red'}>
              {utilizationPercentage}%
            </Badge>
          </Group>
          
          <Progress
            value={utilizationPercentage}
            size="lg"
            radius="md"
            color={utilizationPercentage >= 80 ? 'green' : utilizationPercentage >= 60 ? 'yellow' : 'red'}
            striped={utilizationPercentage < 50}
            animated={utilizationPercentage < 50}
          />
          
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Active: {stats.activeRooms} rooms
            </Text>
            <Text size="sm" c="dimmed">
              Inactive: {stats.inactiveRooms} rooms
            </Text>
          </Group>
        </Stack>
      </Paper>

      {/* Rooms by Floor */}
      {stats.roomsByFloor.length > 0 && (
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={4} fw={600}>
                Rooms by Floor
              </Title>
              <Badge variant="light" color="blue">
                {stats.roomsByFloor.length} floors
              </Badge>
            </Group>
            
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Building</Table.Th>
                    <Table.Th>Floor</Table.Th>
                    <Table.Th>Rooms</Table.Th>
                    <Table.Th>Total Capacity</Table.Th>
                    <Table.Th>Avg. Capacity</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {stats.roomsByFloor.map((floor) => (
                    <Table.Tr key={floor.floorId}>
                      <Table.Td>
                        <Group gap="xs">
                          <IconBuilding size="1rem" color="var(--mantine-color-blue-6)" />
                          <Text fw={500}>{floor.buildingName}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text>{floor.floorName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="blue">
                          {floor.roomCount} room{floor.roomCount > 1 ? 's' : ''}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="green">
                          {floor.totalCapacity} person{floor.totalCapacity > 1 ? 's' : ''}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={500}>
                          {floor.roomCount > 0 ? Math.round(floor.totalCapacity / floor.roomCount) : 0}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Paper>
      )}

      {/* Capacity Distribution */}
      {stats.capacityDistribution.length > 0 && (
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={4} fw={600}>
              Capacity Distribution
            </Title>
            
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {stats.capacityDistribution.map((item, index) => (
                <Paper key={index} p="md" radius="md" withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        {item.capacityRange}
                      </Text>
                      <Badge variant="light" color="blue">
                        {item.count}
                      </Badge>
                    </Group>
                    
                    <Progress
                      value={item.percentage}
                      size="sm"
                      radius="md"
                      color={`blue.${Math.min(6, Math.max(2, Math.floor(item.percentage / 20) + 2))}`}
                    />
                    
                    <Text size="xs" c="dimmed" ta="center">
                      {item.percentage}% of total rooms
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Paper>
      )}

      {/* Summary */}
      <Paper p="lg" radius="md" withBorder bg="var(--mantine-color-blue-0)">
        <Stack gap="sm">
          <Group gap="xs">
            <IconChartBar size="1.25rem" color="var(--mantine-color-blue-6)" />
            <Text fw={600} c="blue">
              Summary
            </Text>
          </Group>
          
          <Text size="sm" c="blue">
            You have {stats.totalRooms} rooms across {stats.roomsByFloor.length} floors with a total capacity of {stats.totalCapacity} people. 
            {stats.activeRooms === stats.totalRooms 
              ? ' All rooms are currently active and available for scheduling.'
              : ` ${stats.activeRooms} rooms are active, while ${stats.inactiveRooms} are currently inactive.`
            }
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
};
